import { useEffect, useMemo, useState } from 'react';

import useDebounce from 'lib/hooks/useDebounce';

import Popover from 'components/shared/Popovers/Popover/Popover';
import Switch from 'components/shared/Switch/Switch';
import Typography from 'components/shared/Typography/Typography';

const SearchInput = ({ defaultFilters, filters, changeFilters }) => {
  const searchConfig = defaultFilters?.search;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFields, setSearchFields] = useState(() =>
    (searchConfig?.fields || []).map((f) => ({ ...f, enabled: true }))
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const enabledFields = useMemo(
    () => searchFields.filter((f) => f.enabled).map((f) => f.field),
    [searchFields]
  );

  useEffect(() => {
    if (!filters?.search) setSearchTerm('');
  }, [filters?.search]);

  useEffect(() => {
    if (!searchConfig) return;

    if (!debouncedSearchTerm) {
      changeFilters((prev) => {
        if (!prev?.search) return prev;
        const { search: _search, ...rest } = prev;
        return rest;
      });
      return;
    }

    changeFilters((prev) => ({
      ...prev,
      search: [
        { type: 'search', values: { searchTerm: debouncedSearchTerm, fields: enabledFields } }
      ]
    }));
  }, [debouncedSearchTerm, enabledFields]);

  const handleToggleField = (field) => {
    setSearchFields((prev) =>
      prev.map((item) => (item.field === field ? { ...item, enabled: !item.enabled } : item))
    );
  };

  if (!searchConfig) return null;

  return (
    <div className="flex h-6 w-36 min-w-36 flex-row items-center gap-1.5 rounded border border-solid border-neutral-200 bg-white py-0 pl-2 pr-1 [&:hover:not(:has([data-headlessui-state=open]))]:bg-neutral-50">
      <input
        type="text"
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="h-full w-full border-none bg-transparent p-0 text-xs outline-none focus:ring-0"
      />
      <Popover
        icon="context-menu-horizontal"
        iconSize={22}
        buttonClassName="!max-h-[22px] !min-w-[22px] !p-0 hover:!bg-neutral-75"
        activeClassNames={{
          button: '!bg-neutral-75 !text-neutral-800 [&_svg_path]:!fill-neutral-800'
        }}
        panelClassName="!min-w-[200px]">
        <div className="flex flex-col gap-3 px-3">
          <Typography variant="p" size="small" weight="semibold" color="neutral-800">
            Search in
          </Typography>
          {searchFields.map((item) => (
            <div key={item.field} className="flex items-center justify-between">
              <Typography variant="p" size="small" color="neutral-800">
                {item.label}
              </Typography>
              <Switch checked={item.enabled} onChange={() => handleToggleField(item.field)} />
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
};

export default SearchInput;
